import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CAMPAIGN_TYPES,
  RECORD_STATUSES,
  CAMPAIGN_CUSTOMER_TYPES,
  CAMPAIGN_MESSAGE_TYPES,
  CAMPAIGN_VARIABLES,
  campaignCancelSchema,
  campaignInputSchema,
  getReportDef,
} from '@app/shared';
import type { CampaignDto, CampaignInput, CampaignType, ReportRow } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { FormDialog } from '../../components/dialogs';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { ApiError, api } from '../../lib/api';
import { Can } from '../auth/guards';
import { withRequiredMessages } from '../inventory/formUtils';
import { ReportScreen, reportQueryKey } from '../reports/ReportPage';

type CampaignForm = Omit<CampaignInput, 'type'> & { type: CampaignType | '' };
const EMPTY: CampaignForm = {
  name: '',
  type: '',
  fromEmail: '',
  fromName: '',
  bccEmail: '',
  subject: '',
  body: '',
  status: 'Active',
};

function CampaignFormBody({ campaign }: { campaign?: CampaignDto }) {
  const notify = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { control, handleSubmit, setError } = useForm<CampaignForm>({
    defaultValues: campaign ? { ...campaign } : EMPTY,
    resolver: withRequiredMessages<CampaignForm>(
      zodResolver(campaignInputSchema) as unknown as Resolver<CampaignForm>,
      { type: 'Select Campaign Type' },
    ),
  });
  const type = useWatch({ control, name: 'type' });
  const save = useMutation({
    mutationFn: (values: CampaignForm) =>
      campaign
        ? api.put<CampaignDto>(`/campaigns/${campaign.id}`, values)
        : api.post<CampaignDto>('/campaigns', values),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: reportQueryKey('campaigns') }),
        queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
      ]),
  });
  const submit = handleSubmit(async (values) => {
    try {
      await save.mutateAsync(values);
      notify.success(campaign ? 'Campaign updated.' : 'Campaign created.');
      navigate('/campaigns');
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      aria-label={campaign ? 'Update Campaign' : 'Create Campaign'}
      onSubmit={(e) => void submit(e)}
      sx={{ p: 2, maxWidth: 820, display: 'grid', gap: 2 }}
    >
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextInput
          control={control}
          name="name"
          label="Campaign Name"
          required
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <SelectInput
          control={control}
          name="type"
          label="Campaign Type"
          required
          // A campaign keeps its type: the fields of the two types differ.
          disabled={Boolean(campaign)}
          emptyLabel="Select"
          options={toOptions(CAMPAIGN_TYPES)}
        />
        {type === 'Email' ? (
          <>
            <TextInput
              control={control}
              name="fromEmail"
              label="From Email Address"
              required
              type="email"
            />
            <TextInput
              control={control}
              name="fromName"
              label="From Email Name"
              slotProps={{ htmlInput: { maxLength: 80 } }}
            />
            <TextInput
              control={control}
              name="bccEmail"
              label="BCC Email Address"
              helperText="Separate multiple BCC email addresses using the comma"
            />
          </>
        ) : null}
        {/* Both types have a subject, a message and a status. */}
        {type ? (
          <>
            <TextInput
              control={control}
              name="subject"
              label="Subject"
              required
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
            <SelectInput
              control={control}
              name="status"
              label="Status"
              options={toOptions(RECORD_STATUSES)}
            />
          </>
        ) : null}
      </Box>
      {/* The reference loads the fields of the chosen type; the message is common to both. */}
      {type ? (
        <TextInput
          control={control}
          name="body"
          label="Message"
          required
          multiline
          minRows={type === 'Email' ? 8 : 4}
          helperText={`Variables: ${CAMPAIGN_VARIABLES.join(' ')}`}
        />
      ) : null}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button type="submit" variant="contained" disabled={save.isPending}>
          Submit
        </Button>
        <Can code="PC762">
          <Button component={Link} to="/campaigns" disabled={save.isPending}>
            Cancel
          </Button>
        </Can>
      </Box>
    </Paper>
  );
}

/** Create Campaign (PC761). */
export function CampaignCreatePage() {
  return (
    <Box>
      <PageHeader title="Create Campaign" code="PC761" />
      <CampaignFormBody />
    </Box>
  );
}

/** "Update Campaign", reached from the campaign list (PC762). */
export function CampaignEditPage() {
  const { id } = useParams();
  const campaign = useQuery({
    queryKey: ['campaigns', id],
    queryFn: () => api.get<CampaignDto>(`/campaigns/${id}`),
    enabled: Boolean(id),
  });
  return (
    <Box>
      <PageHeader title="Update Campaign" code="PC762" />
      {campaign.error ? (
        <ErrorState error={campaign.error} onRetry={() => void campaign.refetch()} />
      ) : !campaign.data ? (
        <LoadingState />
      ) : (
        <CampaignFormBody key={campaign.data.id} campaign={campaign.data} />
      )}
    </Box>
  );
}

/** View Campaign List (PC762). */
export function CampaignListPage() {
  return (
    <ReportScreen
      def={getReportDef('campaigns')!}
      headerActions={
        <Can code="PC761">
          <Button component={Link} to="/campaigns/new" variant="contained">
            Create Campaign
          </Button>
        </Can>
      }
      rowActions={(row) => (
        <MuiLink
          component={Link}
          to={`/campaigns/${String(row.id)}`}
          aria-label={`Update campaign ${String(row.name)}`}
        >
          Update
        </MuiLink>
      )}
    />
  );
}

/* --------------------------- Attach customers (PC763) ---------------------- */

const HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
} as const;

/** An id may not hold spaces: `aria-labelledby` reads them as several ids. */
const idOf = (label: string) => `${label.replace(/\s+/g, '-').toLowerCase()}-label`;

const radios = (
  label: string,
  options: readonly string[],
  value: string,
  onChange: (v: string) => void,
) => (
  <FormControl>
    <FormLabel id={idOf(label)}>{label}</FormLabel>
    <RadioGroup
      row
      aria-labelledby={idOf(label)}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((option) => (
        <FormControlLabel
          key={option}
          value={option}
          label={option}
          control={<Radio size="small" />}
        />
      ))}
    </RadioGroup>
  </FormControl>
);
const yesNo = (label: string, value: string, onChange: (v: string) => void) =>
  radios(label, ['Yes', 'No'], value, onChange);

function AttachForm() {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const campaigns = useQuery({
    queryKey: ['campaigns', 'options'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string; type: string }> }>('/campaigns/options'),
    select: (r) => r.data,
  });
  const [campaignId, setCampaignId] = useState('');
  const [customerType, setCustomerType] = useState<string>(CAMPAIGN_CUSTOMER_TYPES[0]);
  const [messageType, setMessageType] = useState<string>(CAMPAIGN_MESSAGE_TYPES[0]);
  const allActive = customerType === 'All Active';
  const [checkPreference, setCheckPreference] = useState('Yes');
  const [alternateNumber, setAlternateNumber] = useState('No');
  const [scheduleDate, setScheduleDate] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const chosen = (campaigns.data ?? []).find((c) => c.id === campaignId);

  const attach = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('campaignId', campaignId);
      form.append('customerType', customerType);
      form.append('messageType', messageType);
      form.append('checkPreference', checkPreference);
      form.append('alternateNumber', alternateNumber);
      form.append('scheduleDate', scheduleDate);
      if (!allActive && file) form.append('file', file);
      return api.upload<{
        batchId: string;
        successMdn: number;
        failedMdn: number;
        processStatus: string;
      }>('/campaigns/attach', form);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: reportQueryKey('campaign-batches') }),
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    if (!campaignId) found.campaignId = 'Campaign is required';
    if (!allActive && !file) found.file = 'Choose a CSV file';
    setProblems(found);
    if (Object.keys(found).length) return;
    try {
      const done = await attach.mutateAsync();
      notify.success(
        `Batch ${done.batchId}: ${done.successMdn} customer(s) found, ${done.failedMdn} not found. ${done.processStatus}.`,
      );
      setFile(null);
      setScheduleDate('');
    } catch (err) {
      if (err instanceof ApiError && err.details.length)
        setProblems(Object.fromEntries(err.details.map((d) => [d.path, d.message])));
      else setProblems({ file: err instanceof Error ? err.message : 'The file was not accepted.' });
    }
  };

  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      aria-label="Attach customers"
      onSubmit={(e) => void submit(e)}
      sx={{ p: 2, mb: 1.5, display: 'grid', gap: 2, maxWidth: 820 }}
    >
      <TextField
        select
        label="Select Campaign"
        required
        value={campaignId}
        onChange={(e) => setCampaignId(e.target.value)}
        error={Boolean(problems.campaignId)}
        helperText={problems.campaignId}
        sx={{ maxWidth: 400 }}
      >
        <MenuItem value="">Select</MenuItem>
        {(campaigns.data ?? []).map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.name} ({c.type})
          </MenuItem>
        ))}
      </TextField>
      <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {radios('Customer Type', CAMPAIGN_CUSTOMER_TYPES, customerType, setCustomerType)}
        {radios('Message Type', CAMPAIGN_MESSAGE_TYPES, messageType, setMessageType)}
      </Box>
      <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {yesNo('Check Preference', checkPreference, setCheckPreference)}
        {chosen?.type === 'SMS'
          ? yesNo('Send SMS on Alternate Number', alternateNumber, setAlternateNumber)
          : null}
        <TextField
          type="date"
          label="Schedule Date"
          value={scheduleDate}
          onChange={(e) => setScheduleDate(e.target.value)}
          error={Boolean(problems.scheduleDate)}
          helperText={problems.scheduleDate || 'Empty: send now'}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 200 }}
        />
      </Box>
      {allActive ? (
        <FormHelperText>
          Every Active customer of your hierarchy receives the message; no file is needed.
        </FormHelperText>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button component="label" role={undefined} tabIndex={-1} variant="outlined">
              Choose CSV file
              <Box
                component="input"
                type="file"
                accept=".csv,text/csv"
                aria-label="CSV file"
                sx={HIDDEN}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setFile(e.target.files?.[0] ?? null);
                  setProblems({});
                }}
              />
            </Button>
            <Typography>{file ? file.name : 'No file chosen'}</Typography>
            <Button component="a" href={api.url('/campaigns/attach-template')} download>
              Download Sample File
            </Button>
          </Box>
          <FormHelperText error={Boolean(problems.file)}>
            {problems.file ||
              'Note: Please Upload CSV File. Header: MDN, CUSTOMER ID. A row names a customer by either column.'}
          </FormHelperText>
        </>
      )}
      <Box>
        <Button type="submit" variant="contained" disabled={attach.isPending}>
          Submit
        </Button>
      </Box>
    </Paper>
  );
}

function CancelBatchDialog({ batch, onClose }: { batch: ReportRow | null; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset, setError } = useForm<{ reason: string }>({
    defaultValues: { reason: '' },
    resolver: zodResolver(campaignCancelSchema),
  });
  useEffect(() => {
    if (batch) reset({ reason: '' });
  }, [batch, reset]);
  const cancel = useMutation({
    mutationFn: (input: { reason: string }) =>
      api.post(`/campaigns/batches/${String(batch!.id)}/cancel`, input),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: reportQueryKey('campaign-batches') }),
  });
  const submit = handleSubmit(async (values) => {
    try {
      await cancel.mutateAsync(values);
      notify.success('Batch cancelled.');
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <FormDialog
      open={Boolean(batch)}
      title="Cancel Batch"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={cancel.isPending}
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <Typography>Batch {batch ? String(batch.batchId) : ''}</Typography>
        <TextInput
          control={control}
          name="reason"
          label="Cancel Reason"
          required
          autoFocus
          multiline
          minRows={2}
          slotProps={{ htmlInput: { maxLength: 250 } }}
        />
      </Box>
    </FormDialog>
  );
}

/**
 * Attach customers to a campaign (PC763): upload the customers' telephone numbers; the
 * campaign is sent to them now or on the schedule date. The reference form also has two
 * radios (Customer Type, Message Type) whose options were not read; they are left out.
 */
export function CampaignAttachPage() {
  const [cancelling, setCancelling] = useState<ReportRow | null>(null);
  return (
    <>
      <ReportScreen
        def={getReportDef('campaign-batches')!}
        top={<AttachForm />}
        rowActions={(row) =>
          row.processStatus === 'Scheduled' ? (
            <Button
              size="small"
              color="error"
              aria-label={`Cancel batch ${String(row.batchId)}`}
              onClick={() => setCancelling(row)}
            >
              Cancel
            </Button>
          ) : null
        }
      />
      <CancelBatchDialog batch={cancelling} onClose={() => setCancelling(null)} />
    </>
  );
}
